import { Icon, IconButton, Select, Text } from "@assetwatch/ui";
import { ProcedureItemTypeIcon } from "../snowflakes/procedures/ProcedureItemTypeIcon";
import type { ProcedureItem } from "./procedureAuthoring";

export function ProcedureOutlineItem({
  item,
  selected,
  onSelect,
  onMove,
  onDuplicate,
  onDelete,
  onAddAfter,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onMoveToSection,
  sectionId,
  sectionOptions,
  errorKey,
}: {
  item: ProcedureItem;
  selected: boolean;
  onSelect: () => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddAfter: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onMoveToSection: (targetSectionId: string) => void;
  sectionId: string | null;
  sectionOptions: { value: string; label: string }[];
  errorKey?: string;
}) {
  return (
    <li
      className={`flex items-center gap-(--spacing-8) rounded-(--radius-sm) border p-(--spacing-8) ${selected ? "border-outline-primary bg-surface-container-low" : "border-outline-subtle"}`}
      draggable
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragOver();
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDrop();
      }}
    >
      <button
        className="flex min-w-0 flex-1 items-center gap-(--spacing-8) text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
        onClick={onSelect}
        data-procedure-error={errorKey}
        type="button"
      >
        <Icon aria-hidden="true" name="grip-drag" size="xs" />
        <ProcedureItemTypeIcon type={item.type} size="xs" />
        <Text>
          {item.label || "Untitled item"}
          {item.required ? " *" : ""}
        </Text>
      </button>
      {selected ? (
        <>
          <IconButton
            aria-label="Add step below"
            icon="add-plus"
            onClick={onAddAfter}
            size="sm"
            variant="ghost"
          />
          <IconButton
            aria-label="Move item up"
            disabled={!selected}
            icon="chevron-up"
            onClick={onMove.bind(null, -1)}
            size="sm"
            variant="ghost"
          />
          <IconButton
            aria-label="Move item down"
            disabled={!selected}
            icon="chevron-down"
            onClick={onMove.bind(null, 1)}
            size="sm"
            variant="ghost"
          />
          <div className="w-56">
            <Select
              label="Move step to section"
              onChange={(value) => {
                if (value) onMoveToSection(value);
              }}
              options={sectionOptions}
              reserveSubtextSpace={false}
              size="sm"
              value={sectionId ?? "__unsectioned__"}
            />
          </div>
          <IconButton
            aria-label="Duplicate item"
            disabled={!selected}
            icon="copy-regular"
            onClick={onDuplicate}
            size="sm"
            variant="ghost"
          />
          <IconButton
            aria-label="Delete item"
            disabled={!selected}
            icon="trash-delete-bin"
            intent="destructive"
            onClick={onDelete}
            size="sm"
            variant="ghost"
          />
        </>
      ) : null}
    </li>
  );
}
